/* eslint-disable */
import { Dictionary, DictionaryItem } from "@sa/rep-mdm";
import { DictionaryItemsStore } from "@sa/rep-olap";
import { Cell, MDDEResult, Row } from "@sa/rep-mdde";

import { SelectionMode, SortingDirection } from "@sa/rep-common";
import { SortingOptions } from "@sa/rep-mdde";
import { groupBy, orderBy, maxBy } from "lodash";
import { LeakAddTwoTone } from "@material-ui/icons";
import { equal } from "../../../shared";

export type Header = {
  id: string;
  name?: string;
};
export type Column = {
  selection: Record<string, unknown>;
  headers: Header[];
};
export type Point = Record<string, any> & {
  value: number;
  column: Column;
  attributes?: Record<string, string>;
};
export type TransformedRow = {
  selection: Record<string, unknown>;
  attributes: Record<string, string>;
  points: Point[];
};
export type OLAPTransformerResult = {
  columns: Column[];
  rowsHeaders: Header[];
  rows: TransformedRow[];
};

export type Paging = {
  skip: number;
  take: number;
};
export type Filter = {
  id: string;
  selectionMode: SelectionMode;
  selection?: string[];
};
export type OLAPTransformerQuery = {
  lcid: number;
  columns: string[];
  rows: string[];
  cellData: CellDataQuery;
  paging?: Paging;
  sorting?: SortingOptions;
  filters?: Filter[];
};
export type CellDataQuery = {
  inCell: string | string[];
  attributes?: string[];
  metadataHeaderAttribute?: string;
  measuresHeaderAttribute?: string;
};

export type OLAPTransformerOptions = {
  attrsDictionaries: ReadonlyMap<string, Dictionary>;
  attrsNameProxy?: ReadonlyMap<string, (id: unknown, lcid: number) => string>;
  data: MDDEResult;
};
type CellData = {
  selection: Record<string, unknown>;
  columnDef: Record<string, string | string[]>;
};
type RowData = {
  selection: Record<string, unknown>;
  cellMeasure: string | string[];
  cells: Cell[];
};

class DictionaryItemsStoreProxy {
  private readonly _store: DictionaryItemsStore;
  private readonly _cache: Map<string, Promise<void>>;
  private readonly _nameProxy?: ReadonlyMap<string, (id: unknown, lcid: number) => string>;

  constructor(
    attrsDictionaries: ReadonlyMap<string, Dictionary>,
    nameProxy?: ReadonlyMap<string, (id: unknown, lcid: number) => string>,
  ) {
    this._store = new DictionaryItemsStore(attrsDictionaries);
    this._cache = new Map();
    this._nameProxy = nameProxy;
  }

  async fetchItems(aggregatedSelection: Record<string, string[]>) {
    const promises = new Array<Promise<void>>();
    for (const selection in aggregatedSelection) {
      if (Object.prototype.hasOwnProperty.call(aggregatedSelection, selection)) {
        const element = aggregatedSelection[selection];
        const hash = selection + JSON.stringify(element);
        let cacheWaiter = this._cache.has(hash) ? this._cache.get(hash)! : null;
        if (cacheWaiter === null) {
          cacheWaiter = this._store.fetchItems({ [selection]: element });
          this._cache.set(hash, cacheWaiter);
        }
        if (!cacheWaiter) {
          throw new Error("cacheWaiter is null");
        }
        promises.push(
          cacheWaiter.catch((reason) => {
            console.error("fetch error", hash, this._cache, this._store, reason);
          }),
        );
      }
    }
    try {
      return Promise.all(promises);
    } catch (error) {
      console.error(error);
    }
  }

  getItem(dictionaryId: string, itemId: string): DictionaryItem | undefined {
    return this._store.getItem(dictionaryId, itemId);
  }

  getAttributeName(attr: string, lcid: number): string {
    try {
      return this._store.getAttributeName(attr, lcid);
    } catch (err) {
      if (!this._nameProxy || !this._nameProxy.has(attr)) {
        throw err;
      }
    }
    return "";
  }

  getAttributeValue<T>(path: string, itemId: string, lcid: number): T | string | undefined {
    if (this._nameProxy && this._nameProxy.has(path)) {
      return this._nameProxy.get(path)!(itemId, lcid);
    }
    try {
      return this._store.getAttributeValue(path, itemId, lcid);
    } catch (ex) {
      return undefined;
    }
  }
}

export class OLAPTransformer {
  // private readonly _cacheDictionaries: Map<k
  protected readonly _data: MDDEResult;
  protected readonly _dictionaryItemsStore: DictionaryItemsStoreProxy;

  constructor(options: OLAPTransformerOptions) {
    const { attrsDictionaries, data, attrsNameProxy } = options;
    this._data = data;
    this._dictionaryItemsStore = new DictionaryItemsStoreProxy(attrsDictionaries, attrsNameProxy);
  }
  public getData = async (query: OLAPTransformerQuery): Promise<OLAPTransformerResult> => {
    const { aggregatedSelection } = this._data;
    delete aggregatedSelection["Measures"];
    await this._dictionaryItemsStore.fetchItems(aggregatedSelection);
    const columns = this.getColumns(query);
    const rowsHeaders = this.getRowsHeaders(query);
    const rows = this.getRows(query, columns);
    const result: OLAPTransformerResult = {
      columns,
      rowsHeaders,
      rows,
    };
    if (query.sorting) {
      this.sort(result, query.sorting);
    }
    return Promise.resolve(result);
  };

  public sort(result: OLAPTransformerResult, options: SortingOptions) {
    if (options.rowsByValuesSorting) {
      options.rowsByValuesSorting.rules.forEach((rule) => {
        const direction = rule.direction === SortingDirection.Ascending ? "asc" : "desc";
        result.rows = orderBy(
          result.rows,
          (row) => {
            return row.points.reduce((sum, x) => sum + ((rule.measureId && x[rule.measureId]) || x.value), 0);
          },
          direction,
        );
      });
    }

    if (options.columnsByValuesSorting) {
      options.columnsByValuesSorting.rules.forEach((rule) => {
        const direction = rule.direction === SortingDirection.Ascending ? "asc" : "desc";
        result.columns = orderBy(
          result.columns,
          (col) => {
            const points = result.rows
              .filter((row) => {
                for (const selectionId of Object.keys(rule.selection)) {
                  if (Object.keys(row.selection).includes(selectionId)) {
                    if (!equal(row.selection[selectionId], rule.selection[selectionId])) {
                      return false;
                    }
                  }
                }
                return true;
              })
              .flatMap((r) => r.points)
              .filter((x) => x.column === col);
            return points.reduce((sum, x) => sum + ((rule.measureId && x[rule.measureId]) || x.value), 0);
          },
          direction,
        );

        result.rows.forEach((row) => {
          row.points = orderBy(row.points, (p) => result.columns.indexOf(p.column));
        });
      });
    }
  }

  public fetchDictionaries = async () => {
    const { aggregatedSelection } = this._data;

    await this._dictionaryItemsStore.fetchItems(aggregatedSelection);
  };
  public getRowsHeaders = (query: OLAPTransformerQuery): Header[] => {
    const dictionaryItemsStore = this._dictionaryItemsStore;
    const { rows, lcid, cellData } = query;
    const hasCellData = cellData.inCell.length;
    if (hasCellData && !Array.isArray(cellData.inCell)) {
      throw new Error("inCell field must be an array when quering cell data in rows");
    }
    const rowsHeaders = rows.reduce((res: Header[], row) => {
      if (row === "CellData") {
        const { measuresHeaderAttribute = "Measures.Name" } = cellData;
        // TODO: get column name from cube definition
        res.push({ id: measuresHeaderAttribute, name: "Measures" });
        return res;
      }
      const rowName = dictionaryItemsStore.getAttributeName(row, lcid);
      res.push({ id: row, name: rowName });
      return res;
    }, []);
    return rowsHeaders;
  };
  public getColumns = (query: OLAPTransformerQuery): Column[] => {
    const dictionaryItemsStore = this._dictionaryItemsStore;
    const { columns, lcid, cellData } = query;
    // TODO: get columns from result metadata
    const hasCellData = cellData.inCell.length;
    if (hasCellData && !Array.isArray(cellData.inCell)) {
      throw new Error("inCell field must be an array when quering cell data in columns");
    }
    const inCellMeasures = Array.isArray(cellData.inCell)
      ? cellData.inCell.map((measure) => {
          const [_, measureID] = measure.split(".");
          return measureID;
        })
      : [cellData.inCell];
    const inCellMeasuresSet = new Set(inCellMeasures);
    const cells = maxBy(this._data.rows, (row) => row.cells.length)?.cells || [];
    const cellsData = cells.reduce((res: CellData[], cell) => {
      const selection = cell.selection;
      let columnDef: Record<string, string | string[]> = selection;
      if (hasCellData) {
        const Measures = inCellMeasures.filter((measureKey) => inCellMeasuresSet.has(measureKey));
        columnDef = { ...selection, Measures };
      }
      res.push({
        selection,
        columnDef,
      });
      return res;
    }, []);
    const resultColumns = cellsData.map((cell) => {
      const selection = cell.selection;
      const columnDef = cell.columnDef;
      const headers = columns.reduce<any[]>((res, column) => {
        // let columnDictionary = column.split(".")[0];
        const [measureId, hierarchyId, ..._] = column.split(".");
        let columnDictionary = `${measureId}.${hierarchyId}`;
        if (column === "CellData") {
          columnDictionary = "Measures";
        }
        const queryColumn = column === "CellData" ? cellData.measuresHeaderAttribute || "Measures.Name" : column;

        if (columnDictionary !== "Measures") {
          const itemId = columnDef[columnDictionary] as string;
          const columnName = queryColumn
            ? (dictionaryItemsStore.getAttributeValue(queryColumn, `${itemId}`, lcid) as string)
            : itemId;
          res.push({ id: queryColumn, name: columnName });
        } else {
          const itemIds = columnDef[columnDictionary] as string[];
          for (const itemId of itemIds) {
            const columnName = queryColumn
              ? (dictionaryItemsStore.getAttributeValue(queryColumn, `${itemId}`, lcid) as string)
              : itemId;
            res.push({ id: queryColumn, name: columnName });
          }
        }
        return res;
      }, []);
      return {
        selection,
        headers,
      };
    }, []);
    return resultColumns;
  };
  public getRows = (query: OLAPTransformerQuery, columns: Column[]): TransformedRow[] => {
    const dictionaryItemsStore = this._dictionaryItemsStore;
    const { rows: queryRows, lcid, cellData } = query;

    const inCellMeasures = Array.isArray(cellData.inCell)
      ? cellData.inCell.map((measure) => {
          const [_, measureID] = measure.split(".");
          return measureID;
        })
      : [cellData.inCell];

    let dataRows: RowData[];

    if (true) {
      // if measures into rows
      const rowsGroups = Object.values(
        groupBy(this._data.rows, (r) => {
          const hash = { ...r.selection, Measures: undefined };
          return JSON.stringify(hash);
        }),
      );
      const inCellMeasuresSet = new Set(inCellMeasures);
      const Measures = inCellMeasures.filter((measureKey) => inCellMeasuresSet.has(measureKey));
      dataRows = rowsGroups.reduce((res: RowData[], groupRows) => {
        const template = groupRows[0];
        const { inCell } = cellData;
        const row: RowData = {
          selection: { ...template.selection, Measures },
          cellMeasure: inCell,
          cells: [],
        };
        for (const cell of template.cells) {
          const measures = {};
          for (const measurePath of inCell) {
            const [, measureId] = measurePath.split(".");
            const measureRow = groupRows.find((x) => x.selection["Measures"] === measureId);
            const measureCell = measureRow?.cells.find(
              (c) => JSON.stringify(c.selection) === JSON.stringify(cell.selection),
            );
            measures[measureId] = measureCell?.value;
          }
          row.cells.push({ ...cell, ...measures });
        }

        res.push(row);
        return res;
      }, []);
    } else {
      // if measures into columns
      dataRows = this._data.rows.reduce((res: RowData[], row) => {
        const { inCell } = cellData;
        let cells = row.cells;
        if (Array.isArray(inCell)) {
          const rowCellsGroups = groupBy(row.cells, (c) => {
            const hash = { ...c.selection, Measures: undefined };
            return JSON.stringify(hash);
          });
          cells = [] as any[];

          for (const groupKey of Object.keys(rowCellsGroups)) {
            const group = rowCellsGroups[groupKey];
            const measures = {};
            for (const measurePath of inCell) {
              const [, measureId] = measurePath.split(".");
              measures[measureId] = group.find((x) => x.selection["Measures"] === measureId)?.value;
            }

            const inCellMeasuresSet = new Set(inCellMeasures);
            const Measures = inCellMeasures.filter((measureKey) => inCellMeasuresSet.has(measureKey));
            const selection: any = { ...group[0].selection, Measures };
            cells.push({ selection, ...measures });
          }
        }
        res.push({
          selection: row.selection,
          cellMeasure: inCell,
          cells,
        });
        return res;
      }, []);
    }
    const result = dataRows.map((row) => {
      const { selection } = row;
      const attributes = queryRows.reduce((res: Record<string, string>, queryRow) => {
        if (queryRow === "CellData") {
          const [_, measureId] = (row.cellMeasure as string).split(".");
          const { measuresHeaderAttribute = "Measures.Name" } = cellData;
          res[measuresHeaderAttribute] = dictionaryItemsStore.getAttributeValue(
            measuresHeaderAttribute,
            measureId,
            lcid,
          ) as string;
          return res;
        }
        const [dimensionId, hierarchyId, ..._] = queryRow.split(".");
        const rowDict = `${dimensionId}.${hierarchyId}`;
        const itemId = selection[rowDict];
        res[queryRow] = dictionaryItemsStore.getAttributeValue(queryRow, `${itemId}`, lcid) as string;
        return res;
      }, {});
      const points = this._getPointsFromRowDef(row, query, columns);
      return {
        selection,
        attributes,
        points,
      };
    });
    return result;
  };
  private _getPointsFromRowDef(row: RowData, query: OLAPTransformerQuery, columns: Column[]): Point[] {
    const dictionaryItemsStore = this._dictionaryItemsStore;
    const {
      cellData: { attributes },
      lcid,
    } = query;
    const rowSelection = row.selection;
    const columnsHash = new Map(columns.map(column => {
      const hash = JSON.stringify({ ...column.selection, ...rowSelection, Measures: null });
      return [hash, column];
    }));

    const points = row.cells.reduce((res: Point[], cell) => {
      const cellSelection = cell.selection;
      const cellhash = JSON.stringify({ ...cellSelection, ...rowSelection, Measures: null });
      const column = columnsHash.get(cellhash)!;

      if (Array.isArray(row.cellMeasure)) {
        const cellPayload: Point = { value: 0, column };
        if (attributes) {
          const allSelections = { ...rowSelection, ...cellSelection };
          cellPayload.attributes = attributes.reduce((res: Record<string, string>, attr) => {
            const [dimensionId, hierarchyId] = attr.split(".");
            const attrDict = `${dimensionId}.${hierarchyId}`;
            const itemId = allSelections[attrDict];
            res[attr] = dictionaryItemsStore.getAttributeValue(attr, `${itemId}`, lcid) as string;
            return res;
          }, {});
          row.cellMeasure.forEach((measure) => {
            const [_, measureId] = measure.split(".");
            cellPayload[measureId] = cell[measureId];
          });
        } else {
          row.cellMeasure.forEach((measure) => {
            const [_, measureId] = measure.split(".");
            cellPayload[measureId] = cell[measureId];
          });
        }
        res.push(cellPayload);
        return res;
      } else {
        const [_, measureId] = row.cellMeasure.split(".");
        const value = cell[measureId];
        if (attributes) {
          const cellSelection = cell.selection;
          const allSelections = { ...rowSelection, ...cellSelection };
          const pointAttributes = attributes.reduce((res: Record<string, string>, attr) => {
            const [dimensionId, hierarchyId] = attr.split(".");
            const attrDict = `${dimensionId}.${hierarchyId}`;
            const itemId = allSelections[attrDict];
            res[attr] = dictionaryItemsStore.getAttributeValue(attr, `${itemId}`, lcid) as string;
            return res;
          }, {});
          res.push({
            value,
            column,
            attributes: pointAttributes,
          });
          return res;
        }
        res.push({ value, column });
      }
      return res;
    }, []);

    return points;
  }
}
